/**
 * Generate a downloadable PNG report with consultation cards
 */

export async function generateReport(data, beforeImageUrl, heatmapUrl) {
  return new Promise((resolve, reject) => {
    try {
      // Create a canvas for the report
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set canvas size (vertical layout)
      const width = 800;
      const cardHeight = 200;
      const imageHeight = 300;
      const headerHeight = 100;
      const padding = 40;
      const cardSpacing = 20;
      
      const cards = data?.measurements?.three_d_metrics?.consultation?.cards || [];
      const totalHeight = headerHeight + imageHeight + (cards.length * cardHeight) + 
                         (cards.length * cardSpacing) + (padding * 2);
      
      canvas.width = width;
      canvas.height = totalHeight;
      
      // Fill background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, totalHeight);
      
      // Draw header
      ctx.fillStyle = '#1f2937';
      ctx.font = 'bold 32px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Your AI Consultation', width / 2, 50);
      ctx.font = '16px Arial';
      ctx.fillStyle = '#6b7280';
      ctx.fillText('Educational only; not medical advice', width / 2, 80);
      
      let yPos = headerHeight + padding;
      
      // Draw images side by side
      const imageWidth = (width - padding * 3) / 2;
      
      // Load and draw before image
      if (beforeImageUrl) {
        const beforeImg = new Image();
        beforeImg.crossOrigin = 'anonymous';
        beforeImg.onload = () => {
          ctx.drawImage(beforeImg, padding, yPos, imageWidth, imageHeight);
          
          // Draw "Before" label
          ctx.fillStyle = '#374151';
          ctx.font = 'bold 18px Arial';
          ctx.textAlign = 'left';
          ctx.fillText('Before', padding, yPos - 10);
          
          // Load and draw heatmap if available
          if (heatmapUrl) {
            const heatmapImg = new Image();
            heatmapImg.crossOrigin = 'anonymous';
            heatmapImg.onload = () => {
              ctx.drawImage(heatmapImg, padding * 2 + imageWidth, yPos, imageWidth, imageHeight);
              
              // Draw "Depth Map" label
              ctx.fillText('Depth Map', padding * 2 + imageWidth, yPos - 10);
              
              yPos += imageHeight + padding;
              
              // Draw cards
              drawCards(ctx, cards, width, yPos, cardHeight, cardSpacing, padding);
              
              // Convert to blob and download
              canvas.toBlob((blob) => {
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = 'rhinovate-consultation-report.png';
                link.click();
                URL.revokeObjectURL(url);
                resolve();
              }, 'image/png');
            };
            heatmapImg.onerror = () => {
              // Continue without heatmap
              yPos += imageHeight + padding;
              drawCards(ctx, cards, width, yPos, cardHeight, cardSpacing, padding);
              canvas.toBlob((blob) => {
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = 'rhinovate-consultation-report.png';
                link.click();
                URL.revokeObjectURL(url);
                resolve();
              }, 'image/png');
            };
            heatmapImg.src = heatmapUrl;
          } else {
            yPos += imageHeight + padding;
            drawCards(ctx, cards, width, yPos, cardHeight, cardSpacing, padding);
            canvas.toBlob((blob) => {
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = 'rhinovate-consultation-report.png';
              link.click();
              URL.revokeObjectURL(url);
              resolve();
            }, 'image/png');
          }
        };
        beforeImg.onerror = () => {
          reject(new Error('Failed to load before image'));
        };
        beforeImg.src = beforeImageUrl;
      } else {
        reject(new Error('No before image available'));
      }
    } catch (error) {
      reject(error);
    }
  });
}

function drawCards(ctx, cards, width, startY, cardHeight, cardSpacing, padding) {
  let yPos = startY;
  
  cards.forEach((card, index) => {
    // Card background
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;
    ctx.fillRect(padding, yPos, width - padding * 2, cardHeight);
    ctx.strokeRect(padding, yPos, width - padding * 2, cardHeight);
    
    // Region badge
    ctx.fillStyle = '#667eea';
    ctx.fillRect(padding + 20, yPos + 20, 120, 30);
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'left';
    ctx.fillText(
      card.region.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
      padding + 30,
      yPos + 40
    );
    
    // Finding
    ctx.fillStyle = '#374151';
    ctx.font = '16px Arial';
    ctx.textAlign = 'left';
    const findingLines = wrapText(ctx, card.finding, width - padding * 2 - 200, 16);
    findingLines.forEach((line, i) => {
      ctx.fillText(line, padding + 160, yPos + 40 + (i * 20));
    });
    
    // Top suggestion
    if (card.suggestions && card.suggestions.length > 0) {
      ctx.fillStyle = '#6b7280';
      ctx.font = '14px Arial';
      ctx.fillText(
        `• ${card.suggestions[0]}`,
        padding + 160,
        yPos + cardHeight - 30
      );
    }
    
    yPos += cardHeight + cardSpacing;
  });
  
  // Watermark
  ctx.fillStyle = '#d1d5db';
  ctx.font = '12px Arial';
  ctx.textAlign = 'center';
  ctx.fillText(
    'Rhinovate AI - Educational Only',
    width / 2,
    yPos + 20
  );
}

function wrapText(ctx, text, maxWidth, fontSize) {
  const words = text.split(' ');
  const lines = [];
  let currentLine = words[0];
  
  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const width = ctx.measureText(currentLine + ' ' + word).width;
    if (width < maxWidth) {
      currentLine += ' ' + word;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }
  lines.push(currentLine);
  return lines;
}

